<template>
  <div class="compact-header-container" v-if="outlets">
    <div class="header-main-row" @click="toggleExpand">
      <!-- Left: Logo -->
      <div class="logo-circle" v-if="outlets['outlet_logo']">
        <img :src="outlets['outlet_logo']" alt="Logo" class="logo-img" />
      </div>
      
      <!-- Middle: Store Name & Address -->
      <div class="store-text-box">
        <h2 class="store-name">{{ outlets["name"] }}</h2>
        <p class="store-address">{{ outlets["address"] }}</p>
      </div>

      <!-- Right: Toggle Chevron -->
      <div class="toggle-button">
        <span class="toggle-label">info</span>
        <ion-icon 
          :icon="chevronDownOutline" 
          :class="['toggle-chevron', { 'is-expanded': isExpanded }]"
        ></ion-icon>
      </div>
    </div>

    <!-- Collapsible detailed info panel -->
    <transition name="slide-fade">
      <div class="expanded-panel" v-if="isExpanded">
        <div class="panel-content">
          <!-- City & Region -->
          <div class="panel-row">
            <ion-icon :icon="locationOutline" class="panel-icon"></ion-icon>
            <span class="panel-text">{{ outlets["city"] }}, {{ outlets["province"] }}</span>
          </div>

          <!-- Phone WhatsApp Link -->
          <div class="panel-row" v-if="outlets['phone']">
            <ion-icon :icon="logoWhatsapp" class="panel-icon wa-icon"></ion-icon>
            <a :href="whatsappLink" target="_blank" class="panel-link wa-link">
              WhatsApp ({{ outlets["phone"] }})
            </a>
          </div>

          <!-- Status and Working Hours -->
          <div class="panel-row status-row" v-if="hasWorkingHours">
            <span :class="['status-badge', isStoreOpen ? 'open' : 'closed']">
              <span class="status-dot"></span>
              {{ isStoreOpen ? 'Buka' : 'Tutup' }}
            </span>
            <span class="hours-badge" v-if="formattedWorkingHours">
              <ion-icon :icon="timeOutline" class="badge-icon"></ion-icon>
              {{ formattedWorkingHours }}
            </span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useOutletStore } from "@/stores/outlet";
import { IonIcon } from "@ionic/vue";
import { storage } from "../utils/storage";
import { 
  locationOutline, 
  timeOutline, 
  chevronDownOutline,
  logoWhatsapp
} from "ionicons/icons";

export default {
  name: "PandaHome",
  props: ["outlets"],
  components: { IonIcon },
  setup() {
    return {
      locationOutline,
      timeOutline,
      chevronDownOutline,
      logoWhatsapp
    };
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    hasWorkingHours() {
      return this.outlets && this.outlets.working_hour && Array.isArray(this.outlets.working_hour) && this.outlets.working_hour.length > 0;
    },
    getCurrentWorkingHours() {
      if (!this.hasWorkingHours) {
        return null;
      }
      const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      const currentDayName = daysOfWeek[new Date().getDay()];
      const todayHours = this.outlets.working_hour.find(w => w.day.toLowerCase() === currentDayName);
      return todayHours || null;
    },
    isStoreOpen() {
      const hours = this.getCurrentWorkingHours;
      if (!hours) return true; // Default to open if none specified or empty
      
      const now = new Date();
      const currentTime = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
      
      const [oH, oM, oS] = hours.time_open.split(":").map(Number);
      const openSeconds = oH * 3600 + oM * 60 + (oS || 0);

      const [cH, cM, cS] = hours.time_close.split(":").map(Number);
      const closeSeconds = cH * 3600 + cM * 60 + (cS || 0);

      return currentTime >= openSeconds && currentTime <= closeSeconds;
    },
    formattedWorkingHours() {
      const hours = this.getCurrentWorkingHours;
      if (!hours) return "";
      const formatTime = (t) => t.substring(0, 5);
      return `${formatTime(hours.time_open)} - ${formatTime(hours.time_close)}`;
    },
    whatsappLink() {
      if (!this.outlets || !this.outlets.phone) return "";
      let phone = this.outlets.phone.replace(/[^0-9]/g, ""); // strip non-numeric
      if (phone.startsWith("0")) {
        phone = "62" + phone.substring(1);
      }
      return `https://wa.me/${phone}`;
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    setTable() {
      const savedCustomer = storage.getCustomer();
      const tableNo = this.$route.query.table;
      if (tableNo) {
        let customer = {};
        if (savedCustomer) {
          const savedCust = savedCustomer;
          customer = { ...savedCust, table: tableNo }
        } else {
          customer = {
            name: "",
            phone: "",
            table: tableNo,
          }
        }
        storage.setCustomer(JSON.stringify(customer))
      }
    }
  },
  watch: {
    outlets(value) {
      const outletStore = useOutletStore()
      if (this.outlets) {
        outletStore.setStore({ ...value })
      }
    }
  },
  mounted() {
    this.setTable()
  },
};
</script>

<style scoped>
.compact-header-container {
  background: #ffffff;
  padding: 12px 16px;
  border-radius: 16px;
  margin: 10px 0 14px 0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 3px 10px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  text-align: left;
}

.header-main-row {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.logo-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  padding: 2px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
}

.store-text-box {
  flex: 1;
  min-width: 0;
}

.store-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.store-address {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-button {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 12px;
  flex-shrink: 0;
}

.toggle-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
}

.toggle-chevron {
  font-size: 0.85rem;
  color: #64748b;
  transition: transform 0.25s ease;
}

.toggle-chevron.is-expanded {
  transform: rotate(180deg);
}

/* Expanded panel */
.expanded-panel {
  overflow: hidden;
}

.panel-content {
  padding: 12px 4px 4px 60px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.panel-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-icon {
  font-size: 0.9rem;
  color: #64748b;
  flex-shrink: 0;
}

.panel-icon.wa-icon {
  color: #25d366;
  font-size: 1rem;
}

.panel-text {
  font-size: 0.78rem;
  color: #475569;
}

.panel-link {
  font-size: 0.78rem;
  color: #475569;
  text-decoration: none;
  font-weight: 500;
}

.panel-link.wa-link {
  color: #128c7e;
  font-weight: 600;
}

.panel-link:hover {
  text-decoration: underline;
}

.status-row {
  margin-top: 2px;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
}

.status-badge.open {
  background: #ecfdf5;
  color: #10b981;
  border: 1px solid #a7f3d0;
}

.status-badge.closed {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fca5a5;
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}

.status-badge.open .status-dot {
  background-color: #10b981;
}

.status-badge.closed .status-dot {
  background-color: #ef4444;
}

.hours-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: #64748b;
  background: #f8fafc;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.badge-icon {
  font-size: 0.75rem;
}

/* slide-fade transition */
.slide-fade-enter-active {
  transition: all 0.25s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.25s ease-in;
}
.slide-fade-enter-from, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* media query */
@media screen and (max-width: 480px) {
  .compact-header-container {
    margin: 8px 0 12px 0;
    padding: 10px 12px;
  }
}
</style>
